import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, Lightbulb, MessageCircle, Upload, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useHeaderData } from '@/hooks/useHeaderData';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';

export const FEATURE_META: FeatureMeta = {
  id: 'feedback-button',
  label: 'Submit Feedback',
  description: 'Send feedback (profile menu or mobile menu) to report a bug, suggest a feature, or send general feedback. Each submission gets a reference ID and is tracked through a closed-loop status workflow.',
  triggerType: 'dialog',
  personas: ['seller', 'buyer_seller', 'buyer'],
};

type Category = 'bug' | 'feature' | 'general';

const categories: { value: Category; label: string; icon: React.ComponentType<{ className?: string }>; description: string }[] = [
  { value: 'bug', label: 'Bug Report', icon: Bug, description: "Something isn't working" },
  { value: 'feature', label: 'Feature Idea', icon: Lightbulb, description: 'Suggest an improvement' },
  { value: 'general', label: 'General', icon: MessageCircle, description: 'Anything else' },
];

/** Opens the feedback form from any menu (header dropdown, mobile menu). */
export const OPEN_FEEDBACK_EVENT = 'cfx:open-feedback';
export function openFeedback() { window.dispatchEvent(new Event(OPEN_FEEDBACK_EVENT)); }

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

const FeedbackButton = () => {
  useFeatureRegistration(FEATURE_META);
  const { user } = useAuth();
  const location = useLocation();
  const { data: headerData } = useHeaderData();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Category | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null);
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_FEEDBACK_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, onOpen);
  }, []);

  if (!user) return null;

  const resetForm = () => {
    setCategory(null);
    setTitle('');
    setDescription('');
    setScreenshotFile(null);
    setScreenshotPreview(null);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Screenshot must be under 2MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }
    setScreenshotFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setScreenshotPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!category || !title.trim() || !description.trim()) return;
    setSubmitting(true);

    try {
      let screenshot_url: string | null = null;

      // Upload screenshot if provided. Bucket is private — store the storage path,
      // not a public URL. Admins fetch via signed URLs in AdminFeedback.
      if (screenshotFile) {
        const ext = screenshotFile.name.split('.').pop();
        const path = `${user.id}/${Date.now()}.${ext}`;
        const { error: uploadError } = await supabase.storage
          .from('feedback-screenshots')
          .upload(path, screenshotFile);
        if (uploadError) throw uploadError;
        screenshot_url = path;
      }

      const { data: inserted, error } = await supabase.from('user_feedback').insert({
        user_id: user.id,
        company_id: headerData?.companyId ?? null,
        category,
        title: title.trim(),
        description: description.trim(),
        page_url: location.pathname,
        user_agent: navigator.userAgent,
        screenshot_url,
      }).select('id, ref_number').single();

      if (error) throw error;

      toast.success('Thank you! Your feedback has been submitted.');
      resetForm();
      setOpen(false);

      // Fire-and-forget: send acknowledgment email
      if (inserted) {
        supabase.functions.invoke('send-feedback-notification', {
          body: { type: 'submitted', feedback_id: inserted.id },
        }).catch((err) => console.error('Feedback notification error:', err));
      }
    } catch (err: any) {
      console.error('Feedback submission error:', err);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send Feedback</DialogTitle>
            <DialogDescription>
              Help us improve — report a bug, suggest a feature, or share your thoughts.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Category selector */}
            <div className="space-y-2">
              <Label>What's this about?</Label>
              <div className="grid grid-cols-3 gap-2">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const selected = category === cat.value;
                  return (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setCategory(cat.value)}
                      className={cn(
                        'flex flex-col items-center gap-1.5 rounded-lg border p-3 text-xs transition-colors',
                        selected
                          ? 'border-foreground bg-primary text-primary-foreground'
                          : 'border-border bg-card text-muted-foreground hover:border-foreground/30 hover:text-foreground'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="font-medium">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="feedback-title">Title</Label>
              <Input
                id="feedback-title"
                placeholder="Brief summary of your feedback"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={100}
              />
              <p className="text-xs text-muted-foreground text-right">{title.length}/100</p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="feedback-desc">Description</Label>
              <Textarea
                id="feedback-desc"
                placeholder={
                  category === 'bug'
                    ? 'What happened? What did you expect?'
                    : category === 'feature'
                    ? 'Describe the feature you would like to see...'
                    : 'Share your thoughts...'
                }
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground text-right">{description.length}/2000</p>
            </div>

            {/* Screenshot */}
            <div className="space-y-2">
              <Label>Screenshot (optional)</Label>
              {screenshotPreview ? (
                <div className="relative rounded-md border border-border overflow-hidden">
                  <img src={screenshotPreview} alt="Screenshot preview" className="max-h-32 w-full object-contain bg-muted" />
                  <button
                    type="button"
                    onClick={() => { setScreenshotFile(null); setScreenshotPreview(null); }}
                    className="absolute top-1 right-1 rounded-full bg-background/80 p-1 hover:bg-background"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground hover:border-foreground/30 hover:text-foreground transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  Upload image (max 2MB)
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={!category || !title.trim() || !description.trim() || submitting}
              className="w-full"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting…
                </>
              ) : (
                'Submit Feedback'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackButton;
